import React, {Component, ReactNode} from 'react'
import {
    Box,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {RowFlexContainer} from '../../../../../styledComponents/FlexComponents'
import styled from 'styled-components'
import {AssocOfCondominium} from '../../repository/NatGetAssocList'
import {AdminPageHeadingArea} from '../../../../../components/shared/pageBaseComponents/AdminPageHeadingArea'
import {BackLink} from '../../../../../styledComponents/adminPage/Links'
import {AdminPrimaryButton} from '../../../../../styledComponents/adminPage/Buttons'
import NatAdminCommonAcceptedWarningComponent from '../../../common/admin/NatAdminCommonAcceptedWarningComponent'
import {
    AsyncCommunicationProps,
    withAsyncCommunication,
} from '../../../../../components/shared/hoc/withAsyncCommunication'
import EventEmitterExAdmin from '../../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {PUBLISHING_SETTING} from '../../../../../CodeMaster'
import {NatPutReportPublishingSettingRequest} from '../../repository/NatPutReportPublishingSetting'

import {Messages} from '../../../../../Messages'
import {NatAssocWorkReportRepo} from '../../repository/NatAssocWorkReportRepo'
import {Colors} from '../../../../../styledComponents/Constants'

interface Props {
    onCancel: () => void
    onSubmit: (assocInfo: AssocOfCondominium) => void
    assocWorkReportRepo: NatAssocWorkReportRepo
    condominiumCd: string
    assocInfo: AssocOfCondominium
    eventEmitter: EventEmitterExAdmin
}

interface State {
    otherCheckBoxFlag: boolean
    registerButtonDisabled: boolean
    isAccepted: boolean
}

class NatAdminReportPublishingSettingEdit extends Component<Props & AsyncCommunicationProps, State> {
    eventEmitterForDialog: EventEmitterExAdmin

    constructor(props: Readonly<Props & AsyncCommunicationProps>) {
        super(props)

        this.eventEmitterForDialog = new EventEmitterExAdmin()

        this.state = {
            otherCheckBoxFlag:
                this.props.assocInfo.reportPublishingSetting === PUBLISHING_SETTING.PUBLIC.value,
            registerButtonDisabled: true,
            isAccepted: false,
        }
        this.handleChangeOtherCheckBoxFlag = this.handleChangeOtherCheckBoxFlag.bind(this)
        this.handleClickSubmit = this.handleClickSubmit.bind(this)
        this.handleChangeWarningCheckBox = this.handleChangeWarningCheckBox.bind(this)
    }

    handleClickCancel = () => {
        this.props.onCancel()
    }

    handleClickSubmit = async () => {
        const {
            assocWorkReportRepo,
            assocInfo,
            condominiumCd,
            eventEmitter,
            onSubmit
        } = this.props

        const {
            otherCheckBoxFlag,
        } = this.state

        this.eventEmitterForDialog.clearBroadcastedErrors()
        await eventEmitter.showLoading()

        try {
            const request: NatPutReportPublishingSettingRequest = {
                reportPublishingSetting: otherCheckBoxFlag ?
                    PUBLISHING_SETTING.PUBLIC.value : PUBLISHING_SETTING.PRIVATE.value,
                version: assocInfo.version
            }

            await this.props.asyncCallFunction(
                assocWorkReportRepo.updateReportPublishingSetting
                    .bind(assocWorkReportRepo, condominiumCd, assocInfo.assocId, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.ESYCC0004.toString('報告書公開設定')},
            )

            onSubmit(assocInfo)
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleChangeWarningCheckBox(acceptAll: boolean): void {
        this.setState({isAccepted: acceptAll, registerButtonDisabled: !acceptAll})
    }

    handleChangeOtherCheckBoxFlag(event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void {
        this.setState({otherCheckBoxFlag: checked})
    }

    render(): ReactNode {
        const {registerButtonDisabled} = this.state
        const {assocInfo} = this.props

        return (
            <DialogArea>
                <StyledDialogTitle>
                    <AdminPageHeadingArea title={'公開範囲の編集'}/>
                </StyledDialogTitle>
                <StyledDialogContents>
                    <AssocNameArea>
                        {assocInfo.assocName}
                    </AssocNameArea>
                    <Box height={'24px'}/>
                    <span id={'input-term'}>
                        <ItemNameArea>報告書の公開範囲</ItemNameArea>
                        <ItemValueAreaTable>
                            <TableBody>
                                <TableRow>
                                    <NoneBorderTableCell>
                                        <LeftChairmanCheckedBox
                                            color="default"
                                            size={'small'}
                                            checked={true}
                                            disabled
                                        />理事長
                                    </NoneBorderTableCell>
                                </TableRow>
                                <TableRow>
                                    <NoneBorderTableCell>
                                        <CursorPointerLabel>
                                            <LeftCheckbox
                                                color="primary"
                                                size={'small'}
                                                checked={this.state.otherCheckBoxFlag}
                                                onChange={this.handleChangeOtherCheckBoxFlag}
                                            />その他役員
                                        </CursorPointerLabel>
                                    </NoneBorderTableCell>
                                </TableRow>
                            </TableBody>
                        </ItemValueAreaTable>
                    </span>
                    <Box height={'24px'}/>
                    <NatAdminCommonAcceptedWarningComponent
                        description={'内容をご確認いただき、問題なければチェックしたうえで登録ボタンを押してください。'}
                        warnings={[{
                            messageId: 'IASOA0004',
                            message: Messages.IASOA0004.toString(),
                        }]}
                        changeAcceptedWarnings={this.handleChangeWarningCheckBox}
                    />
                </StyledDialogContents>
                <StyledDialogActions>
                    <PageFooterAreaLeft>
                        <BackLink onClick={this.handleClickCancel}>
                            キャンセル
                        </BackLink>
                    </PageFooterAreaLeft>
                    <PageFooterAreaRight>
                        <AdminPrimaryButton
                            onClick={this.handleClickSubmit}
                            disabled={registerButtonDisabled}
                        >
                            登録する
                        </AdminPrimaryButton>
                    </PageFooterAreaRight>
                </StyledDialogActions>
            </DialogArea>
        )
    }
}

export default withAsyncCommunication(NatAdminReportPublishingSettingEdit)

const DialogArea = styled.div`
  min-height: 328px;
  width: 638px;
`

const StyledDialogTitle = withStyles({
    root: {
        padding: '24px 24px 6px 24px',
    },
})(DialogTitle)

const StyledDialogContents = withStyles({
    root: {
        color: Colors.Body,
        padding: '6px 24px 12px 24px',
    },
})(DialogContent)

const AssocNameArea = styled.div`
  background: ${Colors.Background};
  padding: 8px 16px;
  font-weight: bold;
  font-size: 16px;
  overflow-wrap: anywhere;
`

const ItemNameArea = styled.div`
    color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
`

const ItemValueAreaTable = styled(Table)`
  font-size: 14px;
  margin: 8px 0 0 0;
`

const LeftChairmanCheckedBox = withStyles({
    root: {
        padding: '0 4px 8px 0',
        '& .MuiCheckbox-colorPrimary.Mui-checked': {
            color: Colors.AccentDark,
        },
        '&.Mui-disabled': {
            color: Colors.Disabled,
        },
    },
})(Checkbox)

const CursorPointerLabel = styled.label`
  cursor: pointer;
`

const NoneBorderTableCell = styled(TableCell)`
  padding: 0;
    color: rgba(0, 0, 0, 0.87);

  &.MuiTableCell-root {
    border-bottom: none;
  }
`

const LeftCheckbox = withStyles({
    root: {
        padding: '0 4px 8px 0',
    },
})(Checkbox)

const StyledDialogActions = withStyles({
    root: {
        padding: '12px 24px 24px',
    },
})(DialogActions)

const PageFooterAreaLeft = styled(RowFlexContainer)`
  justify-content: flex-start;
  flex-grow: 1;
  align-items: center;
  order: 1;
`

const PageFooterAreaRight = styled(RowFlexContainer)`
  justify-content: flex-end;
  flex-grow: 1;
  align-items: center;
  order: 2;
`
